import React, { useState } from 'react'

import { useTranslation } from 'react-i18next'

import { BulletLabel, Thumbnail } from 'components'
import {
  setValue,
  getImageFileName,
  getPhoneFormatter,
  setPriceFormatter,
  setFormatAssignDate,
  setMaintenancePeriodFormat,
} from 'helpers/common'

import { Branch } from 'features/Branch'
import { Product, WARRANTY_TYPE, CONTACT_TYPE } from 'types'
import SkeletonProductDetail from './SkeletonProductDetail'

import TicketProductWarranty from 'features/Ticket/TicketProductInfo/TicketProductWarranty'
import {
  getDetailByWarrantyPeriod,
  getDetailByWarrantyLifeTime,
} from 'features/Product'
import { getBoxTitle } from 'helpers/language'
import { IconHistory } from 'components/Icons'
import { TicketProductHistoryModal } from 'features/Ticket'

interface Props {
  dataProduct?: Product
  dataBranch: Branch
  loading: boolean
}

interface BoxItemProps {
  title: string | JSX.Element
  description?: string | boolean | number | JSX.Element | null
}

const base = 'f-product-detail'

export const BoxItem = ({ title, description }: BoxItemProps): JSX.Element => {
  return (
    <div className={`${base}__group-text`}>
      <span className={`${base}__title-text`}>{getBoxTitle(title)}</span>
      <span className={`${base}__description-text`}>
        {typeof description === 'string' ? setValue(description) : description}
      </span>
    </div>
  )
}

export const BoxDetailItem = ({
  title,
  description,
}: BoxItemProps): JSX.Element => {
  return (
    <div className={`${base}__group-text`}>
      <span className={`${base}__title-detail-text`}>{getBoxTitle(title)}</span>
      <span className={`${base}__description-text-detail`}>
        {typeof description === 'string' ? setValue(description) : description}
      </span>
    </div>
  )
}

const ProductDetail: React.FC<Props> = ({
  dataProduct,
  dataBranch,
  loading,
}: Props) => {
  const { t } = useTranslation(['product', 'branch', 'company', 'ticket'])
  const [isOpenHistoryModal, setIsOpenHistoryModal] = useState(false)

  const { code, name: branchName, companyName, contacts } = dataBranch

  const phoneNumber = contacts.find(
    (contact) => contact.type === CONTACT_TYPE.PHONE,
  )

  const phone = phoneNumber?.number
    ? getPhoneFormatter(phoneNumber?.number as string)
    : '-'

  const startDate = dataProduct?.startWarranty
    ? setFormatAssignDate(new Date(dataProduct.startWarranty))
    : '-'
  const endDate = dataProduct?.endWarranty
    ? setFormatAssignDate(new Date(dataProduct.endWarranty))
    : '-'
  const _maintenancePeriod = setMaintenancePeriodFormat(
    dataProduct?.maintenancePeriod,
    dataProduct?.maintenanceUnit,
  )

  const _warrantyPeriod = setMaintenancePeriodFormat(
    dataProduct?.warrantyPeriod,
    dataProduct?.warrantyUnit,
  )

  const _salePrice =
    dataProduct &&
    setPriceFormatter(dataProduct.salePrice as number, dataProduct.saleCurrency)
  const _dateOfSale = dataProduct
    ? setFormatAssignDate(new Date(dataProduct.dateOfPurchase))
    : '-'

  const _productWarranty = () => {
    if (dataProduct?.warrantyType === WARRANTY_TYPE.LIFETIME) {
      return [
        {
          title: 'product:warranty.maintenancePeriod.label',
          description: _maintenancePeriod,
        },
        {
          title: 'product:warranty.qrcode.label',
          description: dataProduct?.qrCodeId ? dataProduct?.qrCodeId : '-',
        },
      ]
    } else {
      const warrantyType = dataProduct?.warrantyType.toLocaleLowerCase()

      switch (warrantyType) {
        case WARRANTY_TYPE.PERIOD:
          return getDetailByWarrantyPeriod(
            _warrantyPeriod,
            _maintenancePeriod,
            startDate,
            endDate,
            dataProduct?.qrCodeId,
          )

        case WARRANTY_TYPE.LIFETIME:
          return getDetailByWarrantyLifeTime(
            _maintenancePeriod,
            dataProduct?.qrCodeId,
          )

        default:
          return [
            {
              title: 'product:warranty.qrcode.label',
              description: setValue(dataProduct?.qrCodeId),
            },
          ]
      }
    }
  }

  const branchInfo = [
    { title: 'branch:id.label', description: code },
    { title: 'company:name.label', description: companyName },
    { title: 'branch:name.label', description: branchName },
    {
      title: 'branch:phone.label',
      description: phone,
    },
  ]

  const productInfo = [
    { title: 'product:product.name.label', description: dataProduct?.name },
    {
      title: 'product:status.title',
      description: (
        <BulletLabel
          isActive={dataProduct?.isActive}
          isShowLabel={true}
          className={`${base}__status-description`}
        />
      ),
    },
    {
      title: 'product:product.brand.label',
      description: dataProduct?.brand,
    },
    {
      title: 'product:product.price.label',
      description: _salePrice,
    },
    {
      title: 'product:product.model.label',
      description: dataProduct?.serialNumber,
    },
    {
      title: 'product:product.dateOfPurchase.label',
      description: _dateOfSale,
    },
  ]

  const BranchInfo = () => {
    return (
      <div className={`${base}__branch-info-content`}>
        <div className={`${base}__title-layout`}>
          <span className={`${base}__branch-detail-title`}>
            {t('product:Branch.information.title')}
          </span>
        </div>
        <div className={`${base}__description-layout`}>
          {branchInfo.map((dataProduct, index) => {
            return (
              <BoxItem
                key={index}
                title={dataProduct.title}
                description={dataProduct.description}
              />
            )
          })}
        </div>
      </div>
    )
  }
  const ProductInfo = () => {
    return (
      <div className={`${base}__branch-address-content`}>
        <div className={`${base}__title-warranty-layout`}>
          <span className={`${base}__branch-detail-title`}>
            {t('product:product.title')}
          </span>
          <div className={`${base}__preview-image-content`}>
            <span className={`${base}__title-text`}>
              {t('product:product.previewImage.label')}
            </span>
            {dataProduct?.photos ? (
              <Thumbnail
                src={getImageFileName(dataProduct?.photos[0])}
                className={`${base}__thumbnail`}
              />
            ) : (
              <Thumbnail className={`${base}__thumbnail`} />
            )}
          </div>
        </div>
        <div>
          <div className={`${base}__description-layout`}>
            {productInfo.map((dataProduct, index) => {
              return (
                <BoxItem
                  key={index}
                  title={dataProduct.title}
                  description={dataProduct.description}
                />
              )
            })}
          </div>
          <BoxDetailItem
            title='product:product.detail.label'
            description={dataProduct?.detail}
          />
        </div>
      </div>
    )
  }

  const handleOpenHistoryModal = async () => {
    setIsOpenHistoryModal(!isOpenHistoryModal)
  }

  const ProductWarranty = () => {
    const productWarranty = _productWarranty()

    return (
      <div className={`${base}__branch-info-content`}>
        <div className={`${base}__title-warranty-layout`}>
          <span className={`${base}__warranty-title`}>
            {t('product:warranty.information.title')}
          </span>
          <span className={`${base}__warranty-type-title`}>
            <TicketProductWarranty
              isProduct
              startWarranty={dataProduct?.startWarranty}
              endWarranty={dataProduct?.endWarranty}
              warrantyType={dataProduct?.warrantyType}
            />
            <div
              className={`${base}__history-layout`}
              onClick={() => handleOpenHistoryModal()}
            >
              <IconHistory />
              <span className={`${base}__history`}>{t('ticket:history')}</span>
            </div>

            {isOpenHistoryModal && dataProduct?.id && (
              <TicketProductHistoryModal
                productId={dataProduct.id}
                isOpen={isOpenHistoryModal}
                onClose={() => handleOpenHistoryModal()}
              />
            )}
          </span>
        </div>

        <div className={`${base}__description-layout`}>
          {productWarranty &&
            productWarranty.map((dataProduct, index) => {
              return (
                <BoxItem
                  key={index}
                  title={dataProduct.title}
                  description={dataProduct.description}
                />
              )
            })}
        </div>
      </div>
    )
  }
  return (
    <div className={base}>
      {loading || !dataProduct ? (
        <SkeletonProductDetail />
      ) : (
        <>
          <BranchInfo />
          <ProductInfo />
          <ProductWarranty />
        </>
      )}
    </div>
  )
}

export default ProductDetail
